<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
  directSendEnabled: {
    type: Boolean,
    default: false,
  },
  isDirectSendSelected: {
    type: Boolean,
    default: false,
  },
  isWhatsappTemplateSelected: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['selectDirectSend', 'selectWhatsappTemplate']);

const { t } = useI18n();
const store = useStore();
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const searchQuery = ref('');
const popoverRef = ref(null);

const customTemplates = computed(() => {
  const templates = store.getters['customMessageTemplates/getTemplates'] || [];
  const teamIds = (store.getters['teams/getMyTeams'] || []).map(
    team => team.id
  );

  return templates
    // A Meta template has an external id and is rendered from its official
    // catalog entry below. Only genuinely local templates belong in this list.
    .filter(template => !template.external_id)
    .filter(template => !template.team_id || teamIds.includes(template.team_id))
    .filter(template => !template.archived)
    .filter(template => !template.status || template.status === 'approved')
    .filter(
      template => !template.inbox_id || template.inbox_id === props.inboxId
    )
    .filter(template => {
      const allowed = template.variables?.allowed_in_chat;
      return allowed === true || allowed === 'true';
    })
    .map(template => {
      const components = [{ type: 'BODY', text: template.content }];

      if (template.variables?.footer) {
        components.push({ type: 'FOOTER', text: template.variables.footer });
      }

      if (template.buttons?.length) {
        components.push({
          type: 'BUTTONS',
          buttons: template.buttons.map(button => ({
            text: button.label,
            type: button.type === 'url' ? 'URL' : 'QUICK_REPLY',
            url: button.url,
          })),
        });
      }

      if (template.variables?.format === 'carousel') {
        components.push({
          type: 'CAROUSEL',
          cards: (template.variables.carouselItems || []).map(item => ({
            components: [
              ...(item.imageUrl
                ? [
                    {
                      type: 'HEADER',
                      format: 'IMAGE',
                      example: { header_handle: [item.imageUrl] },
                    },
                  ]
                : []),
              {
                type: 'BODY',
                text: [item.title, item.description].filter(Boolean).join('\n'),
              },
              ...(item.url
                ? [
                    {
                      type: 'BUTTONS',
                      buttons: [
                        {
                          type: 'URL',
                          text: item.buttonText || 'Acessar link',
                          url: item.url,
                        },
                      ],
                    },
                  ]
                : []),
            ],
          })),
        });
      }

      return {
        id: `custom_${template.id}`,
        name:
          template.variables?.meta_name ||
          `${template.name.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_${template.id}`,
        display_name: template.name,
        language: template.variables?.language || template.language || 'pt_BR',
        category: template.category,
        components,
        is_custom: true,
        custom_template_id: template.id,
        // eslint-disable-next-line no-underscore-dangle
        _original_custom_template: template,
      };
    });
});

const whatsAppTemplateMessages = computed(() => {
  const localTemplatesByExternalId = new Map(
    (store.getters['customMessageTemplates/getTemplates'] || [])
      .filter(template => template.external_id)
      .map(template => [String(template.external_id), template])
  );
  const visibleMetaTemplates = (
    getFilteredWhatsAppTemplates.value(props.inboxId) || []
  ).filter(template => {
    const localTemplate = localTemplatesByExternalId.get(String(template.id));
    if (!localTemplate) return true;

    const allowed = localTemplate.variables?.allowed_in_chat;
    return allowed === true || allowed === 'true';
  });

  return [...visibleMetaTemplates, ...customTemplates.value];
});

const filteredTemplates = computed(() => {
  return whatsAppTemplateMessages.value.filter(template =>
    template.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const getTemplateBody = template => {
  return template.components.find(component => component.type === 'BODY')?.text;
};

const getTemplateDisplayName = template =>
  // eslint-disable-next-line no-underscore-dangle
  template._original_custom_template?.name ||
  template.display_name ||
  template.name;

const handlePopoverShow = () => {
  searchQuery.value = '';
};

const handleTemplateClick = (template, hide) => {
  emit('selectWhatsappTemplate', template);
  hide();
};

const handleDirectSend = () => {
  popoverRef.value?.hide();
  emit('selectDirectSend');
};

onMounted(() => {
  store.dispatch('customMessageTemplates/get');
  store.dispatch('teams/get');
});
</script>

<template>
  <div class="flex gap-2 items-center">
    <Button
      v-if="directSendEnabled && !isDirectSendSelected"
      icon="i-lucide-send"
      :label="t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.DIRECT_SEND')"
      color="teal"
      size="sm"
      :disabled="isDirectSendSelected"
      :aria-pressed="isDirectSendSelected"
      class="!text-xs font-medium"
      @click="handleDirectSend"
    />
    <Popover
      v-if="!isWhatsappTemplateSelected"
      ref="popoverRef"
      align="start"
      prefer-above
      disable-mobile-view
      @show="handlePopoverShow"
    >
      <Button
        icon="i-ri-whatsapp-line"
        :label="t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.LABEL')"
        color="slate"
        size="sm"
        class="!text-xs font-medium"
      />
      <template #content="{ hide }">
        <div class="flex flex-col gap-2 p-4 items-center w-[21.875rem]">
          <div class="relative w-full">
            <Icon
              icon="i-lucide-search"
              class="absolute size-3.5 top-2 ltr:left-3 rtl:right-3"
            />
            <input
              v-model="searchQuery"
              type="search"
              :placeholder="
                t(
                  'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.SEARCH_PLACEHOLDER'
                )
              "
              class="w-full h-8 py-2 ltr:pl-10 rtl:pr-10 ltr:pr-2 rtl:pl-2 text-sm reset-base outline-none border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12"
            />
          </div>
          <div
            v-for="template in filteredTemplates"
            :key="template.id"
            class="flex flex-col gap-2 p-2 w-full rounded-lg cursor-pointer dark:hover:bg-n-alpha-3 hover:bg-n-alpha-1"
            @click="handleTemplateClick(template, hide)"
          >
            <span class="text-sm text-n-slate-12">
              {{ getTemplateDisplayName(template) }}
            </span>
            <p class="mb-0 text-xs leading-5 text-n-slate-11 line-clamp-2">
              {{ getTemplateBody(template) }}
            </p>
          </div>
          <template v-if="filteredTemplates.length === 0">
            <p class="pt-2 w-full text-sm text-n-slate-11">
              {{
                t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.EMPTY_STATE')
              }}
            </p>
          </template>
        </div>
      </template>
    </Popover>
  </div>
</template>
